// React
import { gql, useLazyQuery, useMutation, useQuery, useSubscription } from "@apollo/client";
import { useContext, useEffect, useState } from "react";

// Components
import { Box, Card, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PedidoCancelacionAdvertencia from "components/PedidoCancelacionAdvertencia";

// Data
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { MenuContext } from "context/menuContext";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { getUproColor } from "utils/colors";
import { printFactura } from "utils/comanda";
import dataPedidos from "./data/dataPedidos";
import { printComanda } from "utils/comanda";
import ModalBandejas from "../ModalBandejas";
import SoftButton from "components/SoftButton";
import { printTicket } from "utils/comanda";
import { makeNiceText } from "utils/formatters";
import { formatMoneyPunto } from "utils/formatters";
import ModalDetalles from "../ModalDetalles";
import ModalComprobantes from "../ModalComprobantes";
import FacturaImpresion from "./FacturaImpresion";
import {
  detalleEsImprimible,
  detallePerteneceASeccion,
  detalleRequiereImpresionComanda,
  formatearResumenImpresionesFallidas,
  obtenerSeccionesInvolucradas,
  resumirImpresionesFallidasPorComandera,
} from "utils/seccionesComanda";

const notasDeCredito = {
  A: "Nota de crédito A",
  B: "Nota de crédito B",
  V: "Nota de crédito V",
};

const formatTipoCbte = {
  A: 3,
  B: 8,
  V: null,
};

const getPagoRevisionTransferencia = (pedido) =>
  (pedido?.venta?.pagos || []).find(
    (pago) => pago?.estado === "Revision" && pago?.medioDePago?.nombre === "Transferencia"
  ) || null;

const getApolloErrorMessage = (error, fallbackMessage) => {
  const gqlMessage =
    error?.graphQLErrors?.[0]?.message || error?.networkError?.result?.errors?.[0]?.message;
  const rawMessage = gqlMessage || error?.message;
  if (!rawMessage) return fallbackMessage;
  return String(rawMessage).replace(/^GraphQL error:\s*/i, "");
};

function Pedidos({ setRefetchPedidos }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [openModalBandejas, setOpenModalBandejas] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "DESC",
  });
  const [expandFiltroEstado, setExpandFiltroEstado] = useState(false);
  const [filtros, setFiltros] = useState({
    estado: ["PAGO EN REVISIÓN", "RESERVADO", "EN PREPARACIÓN", "ABIERTO"]
  });
  const { menu } = useContext(MenuContext);
  const { user } = useContext(UserContext);
  const { confirmar } = useContext(ConfirmarContext);
  const { handleSnackbar } = useContext(MessageManager);
  const [pedidos, setPedidos] = useState([]);

  const [openModalFactura, setOpenModalFactura] = useState(false); // Estado para controlar el modal
  const [facturaData, setFacturaData] = useState(null); // Datos de la factura
  const [empresaData, setEmpresaData] = useState(null); // Datos d


  const [marcarDetallesComoImpresos] = useMutation(
    gql`
      mutation marcarDetallesComoImpresos($idsDetalles: [ID!]!) {
        marcarDetallesComoImpresos(idsDetalles: $idsDetalles) {
          success
          message
          fechaImpresion
        }
      }
    `
  );

  const { columns, getRows } = dataPedidos;

  const { data: newData } = useSubscription(
    gql`
      subscription {
        nuevoPedido { id updatedAt }
      }
    `
  );

  const { data: modifiedData } = useSubscription(
    gql`
      subscription {
        pedidoModificado { id updatedAt }
      }
    `
  );

  const [prepararPedido, { loading: loadingSave }] = useMutation(
    gql`
      mutation prepararPedido($id: ID!) {
        prepararPedido(id: $id) {
          id
          comentario
          detalles {
            id
            numeroComanda
            cantidad
            precio
            comentario
            producto {
              nombre
              seccion
              id
              comanderas {
                id
                printName
                habilitado
                estadoConexion
              }
            }
          }
        }
      }
    `
  );

  const [finalizarPedido] = useMutation(
    gql`
      mutation finalizarPedido($id: ID!, $bandejas: Int, $observacion: String) {
        finalizarPedido(id: $id, bandejas: $bandejas, observacion: $observacion) {
          id
        }
      }
    `
  );

  const [devolverBandejas] = useMutation(
    gql`
      mutation devolverBandejas($id: ID!) {
        devolverBandejas(id: $id) {
          id
        }
      }
    `
  );

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [deleteVenta, { loading: loadingDelete }] = useMutation(
    gql`
      mutation deleteVenta($id: ID!) {
        deleteVenta(id: $id) {
          id
        }
      }
    `
  );

  const [getVenta] = useLazyQuery(
    gql`
      query getVenta($id: ID!) {
        venta(id: $id) {
          id
          idAnulada
          idPedido
          cliente {
            nombre
            apellido
            appUsuario
          }
          tipoFactura
          fechaFactura
          tipo_cbte
          tipo_doc
          nro_doc
          imp_iva21
          subtotal21
          concepto
          nroAnulado
          url
          pto_vta
          numeroDeFacturaAfip
          CAE
          CAEFchVto
          descuento
          recargo
          total
          mesa
          pedido {
            id
            tipo
            cuentaCorriente
            propina
            detalles {
              producto {
                nombre
              }
              cantidad
              precio
            }
          }
        }
      }
    `
  );

  const { loading, data, refetch } = useQuery(
    gql`
      query getPedidos(
        $filter: JSON
        $search: String
        $page: Int
        $order: Order
        $facturado: String
      ) {
        paginationInfo {
          pages
          total
        }
        pedidos(
          filter: $filter
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
          facturado: $facturado
        ) {
          id
          estado
          comentario
          cuentaCorriente
          pagoSplitCC
          tipo
          total
          createdAt
          updatedAt
          fechaPedido
          bandejas
          bandejasDevueltas
          horaReserva
          turnoReserva
          paraRetirar
          cierreAutomatico
          cliente {
            nombre
            apellido
            appUsuario
          }
          detalles {
            id
            numeroComanda
            cantidad
            precio
            comentario
            producto {
              nombre
              seccion
              id
              comanderas {
                id
                printName
                habilitado
                estadoConexion
              }
            }
            ingredientesQuitados {
              id
            }
            estado
            horarioEntrega
          }
          mesa {
            numero
          }
          venta {
            id
            tipoFactura
            numeroDeFacturaAfip
            idAnulada
            CAE
            idVoucher
            voucher {
              nombreVoucher
              grupo {
                nombre
              }
            }
            pagos {
              id
              totalPago
              estado
              comprobanteFile
              medioDePago {
                nombre
              }
            }
          }
          mozo {
            nombre
            apellido
          }
        }
      }
    `,
    {
      variables: {
        filter: filtros?.fechaPedido
          ? {
            tipo: "Cafetería",
            estado: filtros?.estado || [
              "RESERVADO",
              "EN PREPARACIÓN",
              "CERRADO",
              "ABIERTO",
              "PAGO EN REVISIÓN",
              "PENDIENTE DE PAGO",
              "ANULADO",
            ],
            ...(filtros?.comentario && { comentario: filtros.comentario }),
            _or: [
              {
                fechaPedido: filtros?.fechaPedido,
              },
              {
                createdAt: filtros?.fechaPedido
                  ? {
                    _between: [
                      `${dayjs(filtros.fechaPedido).startOf("day")}`,
                      `${dayjs(filtros.fechaPedido).endOf("day")}`,
                    ],
                  }
                  : undefined,
              },
            ],
          }
          : {
            tipo: "Cafetería",
            estado: filtros?.estado || [
              "RESERVADO",
              "EN PREPARACIÓN",
              "CERRADO",
              "ABIERTO",
              "PAGO EN REVISIÓN",
              "PENDIENTE DE PAGO",
              "ANULADO",
            ],
            ...(filtros?.comentario && { comentario: filtros.comentario }),
          },
        search: search || null,
        page: page,
        facturado: filtros?.facturado || null,
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (refetch) {
      setRefetchPedidos(() => refetch);
    }
  }, [refetch]);

  const [openModalDetalles, setOpenModalDetalles] = useState(false);
  const handleOpenModalDetalles = (item) => {
    setSelectedPedido(item);
    setOpenModalDetalles(true);
  };
  const handleCloseModalDetalles = () => {
    setSelectedPedido();
    setOpenModalDetalles(false);
  };

  const [openModalComprobantes, setOpenModalComprobantes] = useState(false);
  const handleOpenModalComprobantes = () => {
    setOpenModalComprobantes(true);
  };
  const handleCloseModalComprobantes = () => {
    setOpenModalComprobantes(false);
  };

  const ejecutarAnulacionPedido = async (item, preview = null) => {
    const tieneVentaFacturada = Boolean(
      preview?.tieneVentasFacturadas || item?.venta?.numeroDeFacturaAfip
    );

    if (tieneVentaFacturada && item?.venta?.id) {
      await deleteVenta({ variables: { id: item.venta.id } });
      return;
    }

    await anularPedido({
      variables: {
        id: item.id,
        confirmacionEliminarPagosAprobados: true,
      },
    });
  };

  const solicitarAnulacionPedido = async (item) => {
    try {
      const { data: previewData } = await getCancelacionPedidoPreview({
        variables: { id: item.id },
      });

      const preview = previewData?.cancelacionPedidoPreview || null;
      const tienePagosAprobados = Boolean(preview?.tienePagosAprobadosRelacionados);
      const tieneVentaFacturada = Boolean(
        preview?.tieneVentasFacturadas || item?.venta?.numeroDeFacturaAfip
      );

      confirmar({
        title: "Anular pedido",
        icon: tienePagosAprobados ? "warning" : "cancel",
        message: tienePagosAprobados ? (
          <PedidoCancelacionAdvertencia preview={preview} idPedido={item.id} />
        ) : tieneVentaFacturada ? (
          `Esta seguro que desea anular el pedido ${item.id}? Se emitira la nota de credito correspondiente.`
        ) : (
          `Esta seguro que desea anular el pedido ${item.id}?`
        ),
        confirmLabel: tienePagosAprobados ? "Continuar anulación" : "Confirmar",
        cancelLabel: "Cancelar",
        disableBackdropClose: tienePagosAprobados,
        func: () => {
          ejecutarAnulacionPedido(item, preview)
            .then(() => {
              handleSnackbar("Pedido anulado con exito", "success");
              refetch();
            })
            .catch((error) => {
              handleSnackbar(
                getApolloErrorMessage(error, "Error al anular el pedido"),
                "error"
              );
            });
        },
      });
    } catch (error) {
      handleSnackbar(
        getApolloErrorMessage(error, "Error al revisar la anulacion del pedido"),
        "error"
      );
    }
  };

  const [anularPedido] = useMutation(
    gql`
      mutation anularPedido($id: ID!, $confirmacionEliminarPagosAprobados: Boolean) {
        anularPedido(
          id: $id
          confirmacionEliminarPagosAprobados: $confirmacionEliminarPagosAprobados
        ) {
          id
        }
      }
    `
  );

  const [getCancelacionPedidoPreview] = useLazyQuery(
    gql`
      query cancelacionPedidoPreview($id: ID!) {
        cancelacionPedidoPreview(id: $id) {
          tienePagosAprobadosRelacionados
          tieneVentasFacturadas
          mensajeAdvertencia
          pagosAprobados {
            id
            totalPago
            estado
            nombre
            comprobanteFile
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (pedidos) {
      setRows(
        getRows(pedidos, (event, item) => {
          let opciones = [
            {
              name: "Ver factura",
              icon: { icon: "receipt", color: getUproColor("uproGreen") },
              disabled: !item.venta || !item?.venta?.CAE,
              onClick: () => {
                getVenta({ variables: { id: item.venta.id } }).then((res) => {
                  handlePrintFactura(res.data?.venta, user.empresa);
                });
              },
            },
            {
              name: "Ver detalles",
              icon: { icon: "visibility", color: "primary" },
              onClick: () => {
                handleOpenModalDetalles(item);
              },
            },
          ];

          if (item?.venta?.numeroDeFacturaAfip) {
            opciones.push({
              name: "Imprimir ticket",
              icon: { icon: "receipt", color: getUproColor("uproYellow") },
              disabled: !item.venta,
              onClick: async () => {
                await getVenta({ variables: { id: item.venta.id } }).then(async (res) => {
                  const data = {
                    cae: res.data.venta.CAE,
                    vencimientoCae: dayjs(res.data.venta.CAEFchVto).format("DD/MM/YYYY"),
                    url: res.data.venta.url,
                    nroDocumento: formatMoneyPunto(res.data.venta.nro_doc),
                    numeroFacturaAfip: res.data.venta.numeroDeFacturaAfip,
                    tipoFactura: res.data.venta.tipoFactura,
                    mesa: item.mesa?.numero,
                    cliente: res.data.venta.cliente
                      ? makeNiceText(
                        `${res.data.venta.cliente.nombre} ${res.data.venta.cliente.apellido}`,
                        "nombre"
                      )
                      : "Consumidor final",
                    id: res.data.venta.id,
                    url: res.data.venta.url,
                    idPedido: item.id,
                    descuento: res.data.venta.descuento.toFixed(2),
                    recargo: res.data.venta.recargo.toFixed(2),
                    total: res.data.venta.total.toFixed(2),
                  };
                  await printTicket(data, item.detalles).then((response) => {
                    if (response?.status !== 200) {
                      handleSnackbar(
                        `Error al imprimir el ticket ${response?.data?.message ? `: ${response?.data?.message}` : ""
                        }`,
                        "error"
                      );
                    }
                  });
                });
              },
            });
          }

          if (!["CERRADO", "ANULADO"].includes(item.estado)) {
            opciones.push({
              name: "Anular pedido",
              icon: { icon: "cancel", color: getUproColor("uproRed") },
              onClick: () => {
                solicitarAnulacionPedido(item);
              },
            });
          }

          if (item?.estado === "RESERVADO") {
            opciones.push({
              name: "Preparar pedido",
              icon: { icon: "soup_kitchen", color: getUproColor("uproYellow") },
              disabled: !dayjs(item?.fechaPedido).isSame(dayjs().startOf("day")),
              onClick: () => {
                prepararPedido({
                  variables: {
                    id: item.id,
                  },
                })
                  .then(async (data) => {
                    const pedido = data.data.prepararPedido;

                    const detallesTransformados = pedido.detalles.map(
                      ({ numeroComanda, ...resto }) => ({
                        nroComanda: numeroComanda,
                        ...resto,
                      })
                    );

                    const productosNoImpresos = detallesTransformados.filter(detalleRequiereImpresionComanda);

                    if (productosNoImpresos.length > 0) {
                      const seccionesInvolucradas = obtenerSeccionesInvolucradas(productosNoImpresos);


                      let printPromises = [];
                      let comentarioPedido = pedido.comentario || "";

                      if (pedido.paraLlevar) {
                        comentarioPedido = comentarioPedido
                          ? `${comentarioPedido} - ESTE PEDIDO ES PARA LLEVAR`
                          : "ESTE PEDIDO ES PARA LLEVAR";
                      }

                      const mozoInfo = item.mozo
                        ? `${item.mozo.nombre} ${item.mozo.apellido}`
                        : "";
                      const clienteInfo = item.cliente
                        ? `${item.cliente.nombre} ${item.cliente.apellido}`
                        : null;

                      seccionesInvolucradas.forEach((seccion) => {
                        const productosDeSeccion = productosNoImpresos.filter(
                          (detalle) => detalleEsImprimible(detalle) && detallePerteneceASeccion(detalle, seccion)
                        );

                        // Según configuración, definir qué productos enviar a la impresión
                        printPromises.push({
                          promise: printComanda(productosDeSeccion, seccion, comentarioPedido, null, mozoInfo, clienteInfo),
                          seccion,
                          detalles: productosDeSeccion
                        });
                      });

                      const printResults = await Promise.allSettled(printPromises.map(p => p.promise));
                      console.log("Resultados de impresión2:", printResults);

                      const impresionesFallidas = [];
                      let idsDetallesImpresos = [];

                      printResults.forEach((result, index) => {
                        const { seccion, detalles } = printPromises[index];

                        if (result.status === 'fulfilled' && result?.value?.status === 200) {
                          idsDetallesImpresos.push(...detalles.map(d => d.id));
                        } else {
                          const error = result.status === 'fulfilled' ?
                            result.value?.data?.message || 'Error de impresión' :
                            result.reason?.message || 'Error desconocido';
                          impresionesFallidas.push({ seccion, error, detalles });
                        }
                      });

                      if (idsDetallesImpresos.length > 0) {
                        const idsUnicos = [...new Set(idsDetallesImpresos)];

                        try {
                          await marcarDetallesComoImpresos({
                            variables: {
                              idsDetalles: idsUnicos
                            }
                          });
                        } catch (error) {
                          console.error("Error al marcar detalles como impresos:", error);
                        }
                      }

                      if (impresionesFallidas.length === 0) {
                        handleSnackbar("Pedido enviado a preparación", "success");
                      } else {
                        const resumenFallas = resumirImpresionesFallidasPorComandera(impresionesFallidas);
                        const detalleFallas = formatearResumenImpresionesFallidas(resumenFallas);
                        const sufijoFallas = detalleFallas || impresionesFallidas.map((falla) => falla.seccion).join(", ");
                        const prefijo = resumenFallas.length === 1
                          ? "Pedido enviado a preparacion, pero fallo la impresion"
                          : "Pedido enviado a preparacion, pero fallaron impresiones";

                        handleSnackbar(`${prefijo}: ${sufijoFallas}`, "warning");
                      }
                    } else {
                      handleSnackbar("Pedido enviado a preparación", "success");
                    }
                    refetch();
                  })
                  .catch((err) => {
                    handleSnackbar(err.message || "Error al preparar el pedido", "error");
                  });
              },
            });
          } else if (item?.estado === "EN PREPARACIÓN") {
            opciones.push({
              name: "Entregar pedido",
              icon: { icon: "done_all", color: getUproColor("uproGreen") },
              onClick: () => {
                finalizarPedido({
                  variables: {
                    id: item.id,
                    observacion: "Pedido marcado como entregado",
                    // bandejas: value,
                  },
                })
                  .then(() => {
                    handleSnackbar("Pedido finalizado con éxito", "success");
                    refetch();
                    setSelectedPedido();
                  })
                  .catch((err) => {
                    handleSnackbar(err.message || "Error al finalizar el pedido", "error");
                    setSelectedPedido();
                  });
              },
            });
          }

          const pagoRevisionTransferencia =
            item?.estado === "PAGO EN REVISIÓN" ? getPagoRevisionTransferencia(item) : null;
          if (pagoRevisionTransferencia) {
            opciones.push({
              name: "Ver comprobantes",
              icon: { icon: "receipt", color: getUproColor("uproPink") },
              onClick: () => {
                setSelectedPedido(item);
                handleOpenModalComprobantes();
              },
            });
          }

          if (item?.venta?.idAnulada) {
            opciones.push({
              name: "Imprimir nota de crédito",
              icon: { icon: "print", color: getUproColor("uproBlue") },
              disabled: !item.venta.idAnulada,
              onClick: () => {
                getVenta({ variables: { id: item.venta.idAnulada } }).then((res) => {
                  printFactura(res.data.venta, user.empresa);
                });
              },
            });
          }

          menu({
            open: event.currentTarget,
            align: "right",
            options: opciones,
            handleClose: () => { },
          });
        })
      );
    }
  }, [pedidos]);

  useEffect(() => {
    if (data?.pedidos) {
      setPedidos(data.pedidos.filter((pedido) => pedido?.tipo === "Cafetería"));
      setPagesInfo(data.paginationInfo);
    }
  }, [data]);

  useEffect(() => {
    if (newData?.nuevoPedido || modifiedData?.pedidoModificado) refetch();
  }, [newData, modifiedData, refetch]);

  const handleOpenFacturaModal = (factura, empresa) => {
    setFacturaData(factura); // Guardar los datos de la factura
    setEmpresaData(empresa); // Guardar los datos de la empresa
    setOpenModalFactura(true); // Abrir el modal
  };

  const handleCloseFacturaModal = () => {
    setOpenModalFactura(false); // Cerrar el modal
    setFacturaData(null); // Limpiar los datos de la factura
    setEmpresaData(null); // Limpiar los datos de la empresa
  };

  const handlePrintFactura = (venta, empresa) => {
    handleOpenFacturaModal(venta, empresa);
  };
  return (
    <SoftBox>
      <Card>
        <Grid container spacing={2} p={3}>
          <Grid item xs={12}>
            <SoftBox
              display={{
                xs: "flex-row",
                sm: "flex",
              }}
              justifyContent="space-between"
              alignItems="center"
            >
              <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                <SoftTypography variant="h6">{`Pedidos ${filtros?.fechaPedido ? "de hoy" : ""
                  }`}</SoftTypography>
              </SoftBox>
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="end"
                mt={{
                  xs: 2,
                  sm: 0,
                }}
              >
                <SoftBox mr={2}>
                  <Tooltip
                    title={
                      filtros?.fechaPedido === dayjs().format("YYYY-MM-DD")
                        ? "Ver pedidos de todos los días"
                        : "Ver solo pedidos de hoy"
                    }
                    placement="top"
                  >
                    <SoftButton
                      circular
                      iconOnly
                      color={
                        filtros?.fechaPedido === dayjs().format("YYYY-MM-DD")
                          ? "uproYellow"
                          : "uproGreen"
                      }
                      onClick={() => {
                        let filterNew = filtros ? { ...filtros } : {};
                        if (filtros?.fechaPedido) {
                          delete filterNew.fechaPedido;
                        } else {
                          filterNew.fechaPedido = dayjs().format("YYYY-MM-DD");
                        }
                        setFiltros(filterNew);
                        setPage(1);
                      }}
                      sx={{
                        transition: "all 0.5s",
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: "16px !important",
                        }}
                      >
                        {filtros?.fechaPedido === dayjs().format("YYYY-MM-DD")
                          ? "today"
                          : "date_range"}
                      </Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <SoftBox
                  display={{
                    xs: "none",
                    sm: "flex",
                  }}
                  mr={2}
                  alignItems="center"
                >
                  <Tooltip title="Filtrar por estado" placement="top">
                    <SoftButton
                      circular
                      variant="color"
                      color={
                        Array.isArray(filtros?.estado) &&
                          ["PAGO EN REVISIÓN", "RESERVADO", "EN PREPARACIÓN", "ABIERTO"].every((val) =>
                            filtros?.estado?.includes(val)
                          )
                          ? getUproColor("uproGreen")
                          : filtros?.comentario === "UproDay"
                            ? getUproColor("uproPink")
                            : filtros?.estado === "RESERVADO"
                              ? getUproColor("uproYellow")
                              : filtros?.estado === "EN PREPARACIÓN"
                                ? getUproColor("uproPink")
                                : filtros?.estado === "CERRADO" && !filtros?.facturado
                                  ? getUproColor("uproGreen")
                                  : filtros?.estado === "ABIERTO"
                                    ? getUproColor("uproPink")
                                    : filtros?.estado === "CERRADO" && filtros?.facturado
                                      ? getUproColor("error")
                                      : filtros?.estado === "ANULADO"
                                        ? getUproColor("error")
                                        : getUproColor("uproBlue")
                      }
                      sx={{
                        transition: "all 0.5s",
                      }}
                      onClick={(event) => {
                        setExpandFiltroEstado(true);
                        menu({
                          open: event.currentTarget,
                          align: "right",
                          options: [
                            {
                              name: "Todos",
                              icon: { icon: "filter_alt", color: getUproColor("uproBlue") },
                              onClick: () => {
                                let filterNew = filtros ? { ...filtros, facturado: null } : {};
                                if (filtros?.estado) {
                                  delete filterNew.estado;
                                }
                                if (filtros?.comentario) {
                                  delete filterNew.comentario;
                                }
                                setFiltros(filterNew);
                                setPage(1);
                              },
                            },
                            {
                              name: "Pedidos activos",
                              icon: { icon: "play_circle", color: getUproColor("uproGreen") },
                              onClick: () => {
                                setFiltros({
                                  ...filtros,
                                  estado: ["PAGO EN REVISIÓN", "RESERVADO", "EN PREPARACIÓN", "ABIERTO"],
                                  facturado: null,
                                  comentario: undefined
                                });
                                setPage(1);
                              },
                            },
                            {
                              name: "Reservado",
                              icon: { icon: "note_alt", color: getUproColor("uproYellow") },
                              onClick: () => {
                                setFiltros({ ...filtros, estado: "RESERVADO", facturado: null, comentario: undefined });
                                setPage(1);
                              },
                            },
                            {
                              name: "En preparación",
                              icon: { icon: "assignment", color: getUproColor("uproPink") },
                              onClick: () => {
                                setFiltros({
                                  ...filtros,
                                  estado: "EN PREPARACIÓN",
                                  facturado: null,
                                  comentario: undefined,
                                });
                                setPage(1);
                              },
                            },
                            {
                              name: "Cerrado",
                              icon: {
                                icon: "assignment_turned_in",
                                color: getUproColor("uproGreen"),
                              },
                              onClick: () => {
                                setFiltros({ ...filtros, estado: "CERRADO", facturado: null, comentario: undefined });
                                setPage(1);
                              },
                            },
                            {
                              name: "Abierto",
                              icon: { icon: "assignment_late", color: getUproColor("uproPink") },
                              onClick: () => {
                                setFiltros({ ...filtros, estado: "ABIERTO", facturado: null, comentario: undefined });
                                setPage(1);
                              },
                            },
                            {
                              name: "Pago en revisión",
                              icon: { icon: "schedule", color: getUproColor("uproBlue") },
                              onClick: () => {
                                setFiltros({
                                  ...filtros,
                                  estado: "PAGO EN REVISIÓN",
                                  facturado: null,
                                  comentario: undefined,
                                });
                                setPage(1);
                              },
                            },
                            {
                              name: "Pendiente de pago",
                              icon: { icon: "pending", color: getUproColor("uproPink") },
                              onClick: () => {
                                setFiltros({
                                  ...filtros,
                                  estado: "PENDIENTE DE PAGO",
                                  facturado: null,
                                  comentario: undefined,
                                });
                                setPage(1);
                              },
                            },
                            {
                              name: "Sin facturar",
                              icon: { icon: "error", color: getUproColor("error") },
                              onClick: () => {
                                setFiltros({
                                  ...filtros,
                                  estado: "CERRADO",
                                  facturado: "Sin facturar",
                                  comentario: undefined,
                                });
                                setPage(1);
                              },
                            },
                            {
                              name: "Anulados",
                              icon: { icon: "close", color: getUproColor("uproPink") },
                              onClick: () => {
                                setFiltros({
                                  ...filtros,
                                  estado: "ANULADO",
                                  facturado: null,
                                  comentario: undefined,
                                });
                                setPage(1);
                              },
                            },
                            {
                              name: "UPrO Day",
                              icon: { icon: "pending", color: getUproColor("uproPink") },
                              onClick: () => {
                                setFiltros({ estado: "CERRADO", comentario: "UproDay", facturado: null });
                                setPage(1);
                              },
                            }
                          ],
                          handleClose: () => { },
                          onClose: () => {
                            setExpandFiltroEstado(false);
                          },
                        });
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: "16px !important",
                          verticalAlign: "middle",
                          mr: 0.5,
                        }}
                      >
                        {Array.isArray(filtros?.estado) &&
                          ["PAGO EN REVISIÓN", "RESERVADO", "EN PREPARACIÓN", "ABIERTO"].every((val) =>
                            filtros?.estado?.includes(val)
                          )
                          ? "play_circle"
                          : filtros?.comentario === "UproDay"
                            ? "pending"
                            : filtros?.estado === "RESERVADO"
                              ? "note_alt"
                              : filtros?.estado === "EN PREPARACIÓN"
                                ? "assignment"
                                : filtros?.estado === "CERRADO" && !filtros?.facturado
                                  ? "assignment_turned_in"
                                  : filtros?.estado === "ABIERTO"
                                    ? "assignment_late"
                                    : filtros?.estado === "CERRADO" && filtros?.facturado
                                      ? "error"
                                      : "filter_alt"}
                      </Icon>
                      &nbsp;
                      {Array.isArray(filtros?.estado) &&
                        ["PAGO EN REVISIÓN", "RESERVADO", "EN PREPARACIÓN", "ABIERTO"].every((val) =>
                          filtros?.estado?.includes(val)
                        )
                        ? "Pedidos activos"
                        : Array.isArray(filtros?.estado) && filtros?.estado?.length >= 2 ?
                          filtros?.estado.join(", ")
                          : filtros?.comentario === "UproDay"
                            ? "UPrO Day"
                            : filtros?.estado === "CERRADO" && filtros?.facturado
                              ? "Sin facturar"
                              : filtros?.estado || "Todos"}
                      &nbsp;
                      <Icon
                        sx={{
                          rotate: expandFiltroEstado ? "180deg" : "0deg",
                          transition: "all 0.5s",
                          fontSize: "16px !important",
                          verticalAlign: "middle",
                          ml: 0.5,
                        }}
                      >
                        expand_more
                      </Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <SoftBox
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "auto",
                    },
                  }}
                >
                  <SoftInput
                    placeholder="Escribe aquí..."
                    icon={{ component: "search", direction: "left" }}
                    onChange={handleSearch}
                  />
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </Grid>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
        >
          {loading ? (
            <Loading />
          ) : rows?.length > 0 ? (
            <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
          ) : (
            <SinDatos />
          )}
        </SoftBox>
        <CustomPagination
          loading={loading}
          length={data?.pedidos.length}
          total={pagesInfo?.total}
          pages={pagesInfo?.pages}
          page={page}
          setPage={setPage}
          noCard
        />
      </Card>
      <ModalBandejas
        open={openModalBandejas}
        initial={selectedPedido?.bandejas}
        handleClose={() => {
          setOpenModalBandejas(false);
          setSelectedPedido();
        }}
        onConfirm={(value) => {
          setOpenModalBandejas(false);
          finalizarPedido({
            variables: {
              id: selectedPedido.id,
              bandejas: value,
            },
          })
            .then(() => {
              handleSnackbar("Pedido finalizado con éxito", "success");
              refetch();
              setSelectedPedido();
            })
            .catch((err) => {
              handleSnackbar(err.message || "Error al finalizar el pedido", "error");
              setSelectedPedido();
            });
        }}
      />
      <ModalDetalles
        open={openModalDetalles}
        handleClose={handleCloseModalDetalles}
        item={selectedPedido}
      />
      <ModalComprobantes
        open={openModalComprobantes}
        handleClose={handleCloseModalComprobantes}
        data={
          getPagoRevisionTransferencia(selectedPedido) || selectedPedido?.venta?.pagos?.[0] || null
        }
        idPedido={selectedPedido?.id}
        refetch={refetch}
      />
      <Modal
        open={openModalFactura}
        onClose={handleCloseFacturaModal}
        closeAfterTransition
        aria-labelledby="modal-factura-title"
        aria-describedby="modal-factura-description"
      >
        <Fade in={openModalFactura}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              maxHeight: "90%",
              overflowY: "auto",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Card>
              {facturaData && empresaData && (
                <FacturaImpresion factura={facturaData} empresa={empresaData} />
              )}
            </Card>
          </Box>
        </Fade>
      </Modal>
    </SoftBox>
  );
}

Pedidos.propTypes = {
  setRefetchPedidos: PropTypes.func,
};

export default Pedidos;


